import { useState, useEffect, useRef } from 'react';

const TOUCH_DEDUPE_MS = 400;

export const useMultiselectMenuControl = (isMultiSelect) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const lastActionRef = useRef(null);
  const selectWrapperRef = useRef(null);
  const lastTouchEndAtRef = useRef(0);
  const lastTouchEventAtRef = useRef(0);
  const touchStartedOnControlRef = useRef(false);
  const isTouchDeviceRef = useRef(typeof window !== 'undefined' && 'ontouchstart' in window);
  const lastTouchStartWasInsideMenuRef = useRef(false);
  const touchStartedOnChevronRef = useRef(false);
  const menuWasOpenWhenChevronTouchedRef = useRef(false);
  const menuIsOpenRef = useRef(menuIsOpen);

  useEffect(() => {
    if (!isMultiSelect) return;

    const handleControlMouseDown = (event) => {
      if (Date.now() - lastTouchEndAtRef.current < TOUCH_DEDUPE_MS) {
        lastTouchEndAtRef.current = 0;
        return;
      }
      if (selectWrapperRef.current && selectWrapperRef.current.contains(event.target)) {
        const controlElement = selectWrapperRef.current.querySelector('[class*="control"]');
        if (controlElement && controlElement.contains(event.target)) {
          setMenuIsOpen(!menuIsOpen);
        }
      }
    };

    document.addEventListener('mousedown', handleControlMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleControlMouseDown);
    };
  }, [isMultiSelect, menuIsOpen]);

  useEffect(() => {
    const isInsideMenu = (el) => el && (el.closest('[class*="menu"]') || el.closest('[class*="Menu"]'));
    const isInsideOption = (el) => el && (el.closest('[class*="option"]') || el.closest('[class*="Option"]'));

    const handleDocumentClick = (event) => {
      const isControl = event.target.closest('[class*="control"]');
      const isMenu = isInsideMenu(event.target);
      const isOption = isInsideOption(event.target);
      const isSelectWrapper = selectWrapperRef.current && selectWrapperRef.current.contains(event.target);
      const isStickyHeader = event.target.closest('[class*="sticky-header"]');

      const isOnStickyHeaderBackground = isStickyHeader && !isControl && !isSelectWrapper;
      const isOutside = !isControl && !isMenu;
      const isMenuButNotOption = isMenu && !isOption;
      const timeSinceTouch = Date.now() - lastTouchEventAtRef.current;
      const recentlyTouched = timeSinceTouch < TOUCH_DEDUPE_MS;
      const isTouchDevice = isTouchDeviceRef.current;
      const closeForMenuButNotOption = isMenuButNotOption && !isTouchDevice && !recentlyTouched;

      const willClose = (isOutside || isOnStickyHeaderBackground || closeForMenuButNotOption) && menuIsOpen;

      if (willClose) {
        setMenuIsOpen(false);
      }
    };

    const handleDocumentTouchStart = (event) => {
      lastTouchEventAtRef.current = Date.now();
      const isControl = event.target.closest('[class*="control"]');
      const isMenu = isInsideMenu(event.target);
      const isOption = isInsideOption(event.target);
      const isSelectWrapper = selectWrapperRef.current && selectWrapperRef.current.contains(event.target);
      const isStickyHeader = event.target.closest('[class*="sticky-header"]');
      const isOnStickyHeaderBackground = isStickyHeader && !isControl && !isSelectWrapper;
      const isOutside = !isControl && !isMenu;
      const willClose = (isOutside || isOnStickyHeaderBackground) && menuIsOpen;
      const tapOnMenuNotOption = menuIsOpen && isMenu && !isOption;

      lastTouchStartWasInsideMenuRef.current = !!isMenu;

      if (tapOnMenuNotOption) {
        setMenuIsOpen(false);
      } else if (willClose) {
        setMenuIsOpen(false);
      }
    };

    document.addEventListener('click', handleDocumentClick, true);
    document.addEventListener('mousedown', handleDocumentClick, true);
    document.addEventListener('touchstart', handleDocumentTouchStart, true);

    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
      document.removeEventListener('mousedown', handleDocumentClick, true);
      document.removeEventListener('touchstart', handleDocumentTouchStart, true);
    };
  }, [isMultiSelect, menuIsOpen]);

  menuIsOpenRef.current = menuIsOpen;

  useEffect(() => {
    const handleControlTouchStart = (event) => {
      if (!selectWrapperRef.current || !selectWrapperRef.current.contains(event.target)) {
        touchStartedOnControlRef.current = false;
        return;
      }
      if (isMultiSelect) {
        const onClearButton = event.target.closest('[data-multiselect-clear]');
        if (onClearButton) {
          touchStartedOnControlRef.current = false;
          touchStartedOnChevronRef.current = false;
          return;
        }
        const onChevron = event.target.closest('[data-multiselect-chevron]');
        if (onChevron) {
          touchStartedOnControlRef.current = false;
          touchStartedOnChevronRef.current = true;
          menuWasOpenWhenChevronTouchedRef.current = menuIsOpenRef.current;
          return;
        }
      }
      const controlElement = selectWrapperRef.current.querySelector('[class*="control"]');
      const onControl = !!(controlElement && controlElement.contains(event.target));
      touchStartedOnControlRef.current = onControl;
      if (isMultiSelect) touchStartedOnChevronRef.current = false;
    };

    const handleControlTouchEnd = () => {
      if (isMultiSelect && touchStartedOnChevronRef.current) {
        const wasOpen = menuWasOpenWhenChevronTouchedRef.current;
        touchStartedOnChevronRef.current = false;
        lastTouchEndAtRef.current = Date.now();
        if (wasOpen) {
          setMenuIsOpen(false);
        } else {
          setMenuIsOpen(true);
        }
        return;
      }
      if (!touchStartedOnControlRef.current) return;
      touchStartedOnControlRef.current = false;
      lastTouchEndAtRef.current = Date.now();
      setMenuIsOpen((prev) => !prev);
    };

    const handleDocumentTouchEnd = () => {
      lastTouchEventAtRef.current = Date.now();
    };

    document.addEventListener('touchstart', handleControlTouchStart, { passive: true });
    document.addEventListener('touchend', handleControlTouchEnd, { passive: true });
    document.addEventListener('touchend', handleDocumentTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', handleControlTouchStart);
      document.removeEventListener('touchend', handleControlTouchEnd);
      document.removeEventListener('touchend', handleDocumentTouchEnd);
    };
  }, [isMultiSelect]);

  const shouldIgnoreCloseFromReactSelect = () => {
    const ignore = isTouchDeviceRef.current && lastTouchStartWasInsideMenuRef.current;
    lastTouchStartWasInsideMenuRef.current = false;
    return ignore;
  };

  return {
    menuIsOpen,
    setMenuIsOpen,
    selectWrapperRef,
    lastActionRef,
    shouldIgnoreCloseFromReactSelect,
  };
};
